import React, { useState, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {v4 as uuidv4} from 'uuid';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,Autocomplete,
  Button, IconButton, TextField, Avatar, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, Typography, Snackbar, Alert, Badge,
  Box,  Tooltip, Fade, List, ListItem, ListItemText, ListItemSecondaryAction, Drawer
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  //Check as CheckIcon,
  TuneSharp as TuneSharpIcon,
} from '@mui/icons-material';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
//import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
//import RefreshIcon from '@mui/icons-material/Refresh';
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash'; // Added for restoration
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import CloseIcon from '@mui/icons-material/Close';
//import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AutosaveIndicator from './AutoSaveIndicator';
import useSaveOperation from './useSaveOperation';
import {
  discardRisk, moveRiskToSaved,  discardSavedRisk,
  generateRisksWithRAG, generateRiskNarrative
} from './redux/actions/riskActions';

//updateSavedRisk,updateRiskName, 
import { addManualRisk } from './redux/actions/riskActions';
//import _ from 'lodash';
import RefineRiskModal from './RefineRisk'; 
//import IconWithProgress from './IconWithProgress';
import { useAuth0 } from '@auth0/auth0-react';

/** DnDKit Imports **/
import {
  DndContext,
  useDroppable,
  useDraggable,
  useSensor,
  useSensors,
  PointerSensor,
  closestCenter,
  DragOverlay
} from '@dnd-kit/core';
import { restrictToWindowEdges } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';

/** ========= DND Wrappers ========= **/
function DroppableArea({ id, disabled = false, children }) {
  const { isOver, setNodeRef } = useDroppable({ id, disabled });

  return (
    <Box
      ref={setNodeRef}
      sx={{
        // Subtle highlight if user drags an item over this droppable
        backgroundColor: isOver ? 'rgba(0, 127, 255, 0.08)' : 'transparent',
        transition: 'background-color 0.3s ease',
        width: '100%'  // ensure it can fill the container
      }}
    >
      {children}
    </Box>
  );
}

function DraggableRow({ risk, children }) {
  const dragId = risk.riskId || risk.riskName;
  const { attributes, listeners, setNodeRef, transform, transition } = useDraggable({ id: dragId });
  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    cursor: 'grab'
  };

  return (
    <TableRow ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </TableRow>
  );
}
// Floating Buttons for catalogue
const FloatingDraftButton = ({ count, onClick, isPulse }) => (
  <Box sx={{
    backgroundColor: '#FFFFFF',
    boxShadow: 3,
    borderRadius: '50%',
    p: 0.5,
    transform: isPulse ? 'scale(1.2)' : 'scale(1)',
    transition: 'transform 0.3s ease'
  }}>
    <Tooltip title="Show draft risks" arrow>
      <Badge badgeContent={count} color="primary">
        <IconButton onClick={onClick}>
          <SmartToyOutlinedIcon />
        </IconButton>
      </Badge>
    </Tooltip>
  </Box>
);

const FloatingSavedButton = ({ count, onClick, isPulse }) => (
  <Box sx={{
    backgroundColor: '#FFFFFF',
    boxShadow: 3,
    borderRadius: '50%',
    p: 0.5,
    transform: isPulse ? 'scale(1.2)' : 'scale(1)',
    transition: 'transform 0.3s ease'
  }}>
    <Tooltip title="Show saved risks" arrow>
      <Badge badgeContent={count} color="primary">
        <IconButton onClick={onClick}>
          <SaveIcon />
        </IconButton>
      </Badge>
    </Tooltip>
  </Box>
);

const FloatingTrashButton = ({ count, onClick, isPulse }) => (
  <Box sx={{
    backgroundColor: '#FFFFFF',
    boxShadow: 3,
    borderRadius: '50%',
    p: 0.5,
    transform: isPulse ? 'scale(1.2)' : 'scale(1)',
    transition: 'transform 0.3s ease'
  }}>
    <Tooltip title="Discarded risks" arrow>
      <Badge badgeContent={count} color="error">
        <IconButton onClick={onClick}>
          <DeleteIcon />
        </IconButton>
      </Badge>
    </Tooltip>
  </Box>
);

// Trash Modal Component
const TrashModal = ({ open, onClose, discardedRisks, onRestore }) => (
  <Dialog 
    open={open} 
    onClose={onClose}
    maxWidth="sm"
    fullWidth
    TransitionComponent={Fade}
    transitionDuration={300}
  >
    <DialogTitle sx={{ 
      display: 'flex', 
      alignItems: 'center',
      justifyContent: 'space-between',
      borderBottom: 1,
      borderColor: 'divider',
      pb: 2
    }}>
      <Box display="flex" alignItems="center" gap={1}>
        <DeleteForeverIcon color="error" />
        <Typography variant="h6">Discarded Risks</Typography>
      </Box>
      <IconButton onClick={onClose} size="small">
        <CloseIcon />
      </IconButton>
    </DialogTitle>
    
    <DialogContent sx={{ mt: 2 }}>
      {discardedRisks.length > 0 ? (
        <List>
          {discardedRisks.map((risk, index) => (
            <ListItem
              key={risk.riskId || index}
              sx={{
                borderRadius: 1,
                mb: 1,
                '&:hover': {
                  backgroundColor: 'action.hover',
                }
              }}
            >
              <ListItemText
                primary={risk.riskName}
                secondary={`Category: ${risk.category}`}
                primaryTypographyProps={{
                  variant: 'body1',
                  fontWeight: 500
                }}
                secondaryTypographyProps={{
                  variant: 'body2',
                  color: 'text.secondary'
                }}
              />
              <ListItemSecondaryAction>
                <Tooltip title="Restore risk" arrow>
                  <IconButton 
                    edge="end" 
                    onClick={() => onRestore(risk)}
                    size="small"
                    sx={{
                      '&:hover': {
                        color: 'primary.main'
                      }
                    }}
                  >
                    <RestoreFromTrashIcon />
                  </IconButton>
                </Tooltip>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      ) : (
        <Box 
          display="flex" 
          alignItems="center" 
          justifyContent="center" 
          minHeight={200}
          flexDirection="column"
          gap={2}
        >
          <DeleteForeverIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
          <Typography color="text.secondary" variant="body1">
            No discarded risks
          </Typography>
        </Box>
      )}
    </DialogContent>
    
    <DialogActions sx={{ 
      borderTop: 1, 
      borderColor: 'divider',
      px: 3,
      py: 2
    }}>
      <Button 
        onClick={onClose}
        variant="outlined"
        startIcon={<CloseIcon />}
      >
        Close
      </Button>
    </DialogActions>
  </Dialog>
);

const RiskCatalogue = ({
    modelId,
    sectionName,
    assessmentUuid,
    narratives: modelNarratives,
    sectionLabel,
    filterSource,     // ← already consumed later in file
    hideHeader = false,
  }) => {
  const dispatch = useDispatch();
  // Local states
  // const [editingRisk, setEditingRisk] = useState(null);
  // const [editedRiskName, setEditedRiskName] = useState('');
  const [confirmDiscardRisk, setConfirmDiscardRisk] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');
  const [showTrashModal, setShowTrashModal] = useState(false);
  const [openDiscardDialog, setOpenDiscardDialog] = useState(false);
  const [openRefineModal, setOpenRefineModal] = useState(false);
  const [riskToRefine, setRiskToRefine] = useState(null);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [newRiskName, setNewRiskName] = useState('');
  const [newRiskCategory, setNewRiskCategory] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRisk, setSelectedRisk] = useState(null);
  const [filterMode, setFilterMode] = useState('default');   
  const { isSaving, isSaved, handleSaveOperation, setIsSaved } = useSaveOperation();

  // Initialize draft risks on mount
  // Legacy initializeDraftRisks removed - sources no longer auto-convert to draft risks

  // selectors
  const risksData = useSelector(
    state => state.risks.models[modelId]?.riskFormData[sectionName]?.sectionRisks
  );
  const risks = useMemo(() => risksData || {}, [risksData]);
  const riskNarratives = useSelector(s => s.risks.models[modelId]?.riskNarratives || {});
  const modelMeta = useSelector(state => state.risks.models[modelId] || {});
  const { modelName} = modelMeta;

  const getUnsavedRisks = () => {
    const unsavedRisks = [];
    Object.values(risks).forEach((riskArray) => {
      riskArray.forEach((risk) => {
        if (!risk.riskId &&  risk.status === 'Draft') {
          unsavedRisks.push(risk);
        }
      });
    });
    return unsavedRisks;
  };

  const unsavedRisks = getUnsavedRisks();
  const { user } = useAuth0();
 



  // For DnD
  const [activeRisk, setActiveRisk] = useState(null);

  // Loading states
  // const [loadingLLM, setLoadingLLM] = useState(false);
  // const [loadingRAG, setLoadingRAG] = useState(false);
  //const [hasLLMDraft, setHasLLMDraft] = useState(false);

  // Animation trigger states
  const [triggerGeneratePulse, setTriggerGeneratePulse] = useState(false);
  const [triggerSavePulse, setTriggerSavePulse] = useState(false);
  const [triggerTrashPulse, setTriggerTrashPulse] = useState(false);

  const triggerGenerateAnimation = () => {
    setTriggerGeneratePulse(true);
    setTimeout(() => setTriggerGeneratePulse(false), 500);
  };
  const triggerSaveAnimation = () => {
    setTriggerSavePulse(true);
    setTimeout(() => setTriggerSavePulse(false), 500);
  };
  const triggerTrashAnimation = () => {
    setTriggerTrashPulse(true);
    setTimeout(() => setTriggerTrashPulse(false), 500);
  };

  const uniqueRisksArray = useMemo(() => {
    const chosen = new Map();                          // key = name::category
  
    Object.entries(risks).forEach(([category, arr]) => {
      arr.forEach(risk => {
        const key   = `${risk.riskName}::${category}`.toLowerCase();
        const prev  = chosen.get(key);
  
        /* keep the Saved one if it exists – otherwise keep whatever we saw first */
        if (!prev || (prev.status !== 'Saved' && risk.status === 'Saved')) {
          chosen.set(key, { ...risk, category });
        }
      });
    });
  
    return Array.from(chosen.values());                // flat array
  }, [risks]);

  const countSavedRisks     = useCallback(() => uniqueRisksArray.filter(r => r.status === 'Saved').length,  [uniqueRisksArray]);
const countDraftRisks     = useCallback(() => uniqueRisksArray.filter(r => r.status === 'Draft').length,  [uniqueRisksArray]);
const countDiscardedRisks = useCallback(() => uniqueRisksArray.filter(r => r.status === 'Discarded').length, [uniqueRisksArray]);


   
  const effectiveFilter = useMemo(() => {
    if (filterMode === 'default') {
      return countSavedRisks() ? 'saved' : 'draft';
    }
    return filterMode;                                         // explicit choice
  }, [filterMode, countSavedRisks]);
  
  const displayRisks = useMemo(() => {
    const grouped = {};
  
    uniqueRisksArray.forEach(risk => {
      const show =
        effectiveFilter === 'all' ||
        risk.status.toLowerCase() === effectiveFilter ||
        risk.status === 'Discarded';          //  ⬅︎ always keep Discarded visible
  
      if (!show) return;
  
      if (!grouped[risk.category]) grouped[risk.category] = [];
      grouped[risk.category].push(risk);
    });
  
    return grouped;                                           // {category:[…]}
  }, [uniqueRisksArray, effectiveFilter]);

  const toggleFilter = mode =>
    setFilterMode(prev => (prev === mode ? 'default' : mode));
 

  const riskAreaData = useSelector(
    (state) => state.risks.models[modelId]?.riskFormData[sectionName]?.riskSources || []
  );

  const categoryOptions = useMemo(() => {
     const fromRisks   = Object.keys(risks);
    const fromSources = riskAreaData.map(rs => rs.name);
  // merge, dedupe, drop empty
    return Array.from(new Set([...fromRisks, ...fromSources])).filter(Boolean);
    }, [risks, riskAreaData]);



  const findRiskById = (id) => {
    const allRisks = Object.values(risks).flat();
    return allRisks.find((r) => (r.riskId || r.riskName) === id);
  };

  /** ======= DnDKit config & handlers ======= **/
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleDragStart = ({ active }) => {
    const foundRisk = findRiskById(active.id);
    setActiveRisk(foundRisk || null);
  };

  const handleDragEnd = ({ active, over }) => {
    if (!over?.id || !active?.id) {
      setActiveRisk(null);
      return;
    }
    const draggedRisk = findRiskById(active.id);
    if (!draggedRisk) {
      setActiveRisk(null);
      return;
    }

    if (over.id === 'savedRisks') {
      handleSaveRisk(draggedRisk);
      triggerSaveAnimation(); // Trigger save pulse animation
    } else if (over.id === 'discardedRisks') {
      handleDiscardRisk(draggedRisk);
      triggerTrashAnimation(); // Trigger trash pulse animation
    }
    setActiveRisk(null);
  };

  const handleDragCancel = () => {
    setActiveRisk(null);
  };



  const handleSaveRisk = useCallback(
    async (risk) => {
      // Ensure every risk has an ID, generating a new one if needed
      const id = risk.riskId || uuidv4();
  
      // Wrap the network + state update in the autosave handler
      handleSaveOperation(async () => {
        // Build the payload to match the saveRisk backend function
        const payload = {
          riskId: id,
          name: risk.riskName,
          section: sectionName,
          category: risk.category,
          type: risk.type,
          rag_source_file: risk.type === 'RAG' ? risk.rag_source_file : null,
          modelUuid: modelId,
          assessmentUuid,
          owner: {
            name: user.name,
            email: user.email,
            picture: user.picture,
          }
        };
  
        const response = await fetch('/.netlify/functions/saveRisk', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
  
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to save risk');
        }
  
        const { riskId: returnedId } = await response.json();
  
        // Move into saved state with the same ID (or backend-returned one)
        dispatch(
          moveRiskToSaved(modelId, sectionName, {
            ...risk,
            riskId: returnedId || id,
            status: 'Saved',
            owner: {
              name: user.name,
              email: user.email,
              picture: user.picture
            },
            type: risk.type,
            rag_source_file: payload.rag_source_file
          })
        );
      }).catch((error) => {
        setSnackbarMessage(error.message);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      });
    },
    [handleSaveOperation, dispatch, modelId, sectionName, assessmentUuid, user.name, user.email, user.picture]
  );
  const handleDiscardRisk = useCallback(
    async (risk) => {
      if (risk.riskId) {
        const response = await fetch('/.netlify/functions/discardRisk', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ riskId: risk.riskId })
        });
        if (!response.ok) {
          throw new Error('Failed to discard the risk');
        }
        dispatch(discardSavedRisk(risk.riskId, modelId, sectionName, risk.category));
      } else {
        dispatch(discardRisk(risk, modelId, sectionName, risk.category));
      }
    },
    [dispatch, modelId, sectionName]
  );

  const discardUnsavedRisks = useCallback(() => {
    Object.entries(risks).forEach(([category, risksArray]) => {
      risksArray.forEach((risk) => {
        if (risk.status === 'Draft') {
          dispatch(discardRisk(risk, modelId, sectionName, risk.category));
        }
      });
    });
    setOpenDiscardDialog(false);
  }, [dispatch, risks, modelId, sectionName]);

  /** ======= Edit logic ======= **/
  // const handleEditRisk = useCallback((risk) => {
  //   setEditingRisk(risk);
  //   console.log('handleEditRisk => risk.category is:', risk.category);
  //   setEditedRiskName(risk.riskName);
  // }, []);

  // const handleConfirmEdit = useCallback(async () => {
  //   if (!editingRisk) return;
  //   console.log ('risk to edit in handleconfirmedit', editingRisk)
  //   handleSaveOperation(async () => {
  //     if (editingRisk.status === 'Saved') {
  //       const response = await fetch('/.netlify/functions/saveRisk', {
  //         method: 'POST',
  //         headers: { 'Content-Type': 'application/json' },
  //         body: JSON.stringify({
  //           riskId: editingRisk.riskId,
  //           name: editedRiskName,
  //           section: sectionName,
  //           category: editingRisk.category,
  //           type: editingRisk.type,
  //           rag_source_file: editingRisk.type === 'RAG' ? editingRisk.rag_source_file : null,
  //           modelUuid: modelId,
  //           assessmentUuid: assessmentUuid,
  //           owner: {
  //             name: user.name,
  //             email: user.email,
  //             picture: user.picture
  //           }
  //         })
  //       });
  //       if (!response.ok) {
  //         const errorData = await response.json();
  //         throw new Error(errorData.error || 'Failed to update risk');
  //       }
  //       dispatch(
  //         updateSavedRisk(modelId, editingRisk.riskId, sectionName, editingRisk.category, {
  //           ...editingRisk,
  //           riskName: editedRiskName
  //         })
  //       );
  //     } else {
  //       dispatch(
  //         updateRiskName(
  //           modelId,
  //           sectionName,
  //           editingRisk.category,
  //           editingRisk.riskName,
  //           editedRiskName
  //         )
  //       );
  //     }
  //   }).finally(() => {
  //     setEditingRisk(null);
  //     setEditedRiskName('');
  //   });
  // }, [
  //   modelId,
  //   sectionName,
  //   editingRisk,
  //   editedRiskName,
  //   handleSaveOperation,
  //   dispatch,
  //   assessmentUuid,
  //   user.name,
  //   user.email,
  //   user.picture
  // ]);

  /** ======= Counts & placeholders ======= **/
  
  const renderPlaceholder = (text) => (
    <TableRow>
      <TableCell colSpan={5} sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
        {text}
      </TableCell>
    </TableRow>
  );
  const renderRiskRow = (risk, category) => (
    <DraggableRow key={risk.riskId || `${risk.riskName}-${category}`} risk={risk}>
      <TableCell>{risk.riskName}</TableCell>
      <TableCell>{risk.status}</TableCell>
      <TableCell>
        {risk.owner ? (
          <Tooltip title={risk.owner.name}>
            <Avatar alt={risk.owner.name} src={risk.owner.picture} />
          </Tooltip>
        ) : 'No owner'}
      </TableCell>
   
      {/* New Actions cell: Refine / Save / Discard */}
      <TableCell align="center">
            
            <Tooltip title="Refine">
              <IconButton onClick={() => { setRiskToRefine(risk); setOpenRefineModal(true); }}>
                <SmartToyOutlinedIcon />
              </IconButton>
            </Tooltip>
            {risk.status === 'Draft' && (
              <Tooltip title="Save Risk">
                <IconButton onClick={() => handleSaveRisk(risk)}>
                  <SaveIcon />
                </IconButton>
              </Tooltip>
            )}
            {risk.status === 'Saved' ? (
              <Tooltip title="Discard Risk">
                <IconButton onClick={() => handleDiscardRisk(risk)}>
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title="Save first to discard">
                <span>
                  <IconButton disabled>
                    <DeleteIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {risk.status === 'Saved' && (
              <Tooltip title="Risk Events & Scenarios">
                <IconButton onClick={() => { setSelectedRisk(risk); setDrawerOpen(true); }}>
                  <TuneSharpIcon />
                </IconButton>
              </Tooltip>
            )}
          </TableCell>
        </DraggableRow>
      );
  


  const handleDiscardClick = () => {
    if (unsavedRisks.length === 0) {
      // Optionally, notify the user that there are no unsaved risks to discard
      return;
    }
    setOpenDiscardDialog(true);
  };

  const handleConfirmDiscard = () => {
    discardUnsavedRisks();
    setSnackbarMessage('All unsaved risks have been discarded.');
    setSnackbarSeverity('info');
    setSnackbarOpen(true);
  };

  const handleCancelDiscard = () => {
    setOpenDiscardDialog(false);
  };
  // const handleOpenRefineModal = (risk) => {
  //   setRiskToRefine(risk);
  //   setOpenRefineModal(true);
  // };
  
  const handleCloseRefineModal = () => {
    setRiskToRefine(null);
    setOpenRefineModal(false);
  };

  /** ======= Table Display Logic ======= **/
  const areaLabel = useMemo(() => {
    return sectionName
      .replace(/([A-Z])/g, ' $1')
      .replace(/[_-]/g, ' ')
      .replace(/\b\w/g, ch => ch.toUpperCase())
      .trim();
  }, [sectionName]);
  /** ======= UI / Return ======= **/
  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
      modifiers={[restrictToWindowEdges]}
    >
      <Box sx={{ backgroundColor: '#F9FAFB', p: 3, position: 'relative', minHeight: '100%' }}>
        {/* === Header on gray background === */}
        {!hideHeader && (
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          mb: 2,
        }}>
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 600,
              color: '#374151', // Slightly lighter than main titles
              fontSize: '1rem', // 16px - more appropriate for section headers
              letterSpacing: '-0.01em',
              lineHeight: 1.4,
              ml:1
            }}
          >
            Risk Identification — {sectionLabel}
          </Typography>
          <Box sx={{ ml: 1 }}>
            <AutosaveIndicator isSaving={isSaving} isSaved={isSaved} setIsSaved={setIsSaved} />
          </Box>
        </Box>
         )}

        {/* === Gray Content Container === */}
        <Box sx={{
          backgroundColor: '#F9FAFB',
          p: 3,
          borderRadius: 1,
          border: '1px solid #E5E7EB',
        }}>
          {/* Section Title */}
          <Typography variant="h6" sx={{ mb: 1 }}>
            {filterMode === 'saved' ? `${areaLabel} Risks` : 'Suggested Risks'}
          </Typography>
          {/* Buttons and floating icons combined */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mt={2} mb={2}>
            <Box display="flex" gap={2}>

              <Button
                onClick={() => setOpenAddDialog(true)}
                startIcon={<EditIcon />}
                size="small"
                variant="contained"
                sx={{
                  height: 48,
                  backgroundColor:'#111827',
                  color:'#FFFFFF',
                  '&:hover':{ backgroundColor:'#1F2937' },
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 1.5,
                  px: 2.5
                }}
              >
                Add
              </Button>
            </Box>
            <Box display="flex" alignItems="center" gap={2}>
                          <FloatingDraftButton
                count={countDraftRisks()}
                isPulse={triggerGeneratePulse}
                onClick={() => { triggerGenerateAnimation(); toggleFilter('draft'); }}
              />

              <FloatingSavedButton
                count={countSavedRisks()}
                isPulse={triggerSavePulse}
                onClick={() => { triggerSaveAnimation(); toggleFilter('saved'); }}
              />

              <FloatingTrashButton
                count={countDiscardedRisks()}
                isPulse={triggerTrashPulse}
                onClick={() => { triggerTrashAnimation(); toggleFilter('discarded'); }}
              />
            </Box>
          </Box>

          {/* {(loadingLLM || loadingRAG) && (
            <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
              Loading...
            </Typography>
          )} */}

        {/* Manual Entry Dialog */}
        <Dialog open={openAddDialog} onClose={() => setOpenAddDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Add Risk</DialogTitle>
          <DialogContent>
            <TextField
              label="Risk Name"
              value={newRiskName}
              onChange={(e) => setNewRiskName(e.target.value)}
              fullWidth
              margin="normal"
            />
            <Autocomplete
              freeSolo
              options={categoryOptions}
              value={newRiskCategory}
              onInputChange={(_, v) => setNewRiskCategory(v)}
              renderInput={(params) => <TextField {...params} label="Category" fullWidth margin="normal" />}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
                setOpenAddDialog(false);
                setNewRiskName('');
                setNewRiskCategory('');
              }}>
              Cancel
            </Button>
            <Button onClick={() => {
                dispatch(addManualRisk(
                  modelId, 
                  sectionName, 
                  newRiskName.trim(),
                  newRiskCategory.trim() || (categoryOptions[0] || 'General')
                ));
                setOpenAddDialog(false);
                setNewRiskName('');
                setNewRiskCategory('');
              }} variant="contained">
              Add
            </Button>
          </DialogActions>
        </Dialog>
        {/* === Risks Table === */}
        <DroppableArea id="draftRisks" disabled>
          <Box sx={{
            backgroundColor: '#FFFFFF',
            borderRadius: 1,
            border: '1px solid #E5E7EB',
            overflow: 'hidden',
            mt: 2,
          }}>
            <TableContainer>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ backgroundColor: '#F9FAFB', color: '#6B7280', fontWeight: 500 }}>Risk Name</TableCell>
                    <TableCell sx={{ backgroundColor: '#F9FAFB', color: '#6B7280', fontWeight: 500 }}>Status</TableCell>
                    <TableCell sx={{ backgroundColor: '#F9FAFB', color: '#6B7280', fontWeight: 500 }}>Owner</TableCell>
                    <TableCell align="center" sx={{ backgroundColor: '#F9FAFB', color: '#6B7280', fontWeight: 500 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
             
                <TableBody>
                    {Object.keys(displayRisks).length ? (
                      Object.entries(displayRisks).map(([category, arr]) => (
                        <React.Fragment key={category}>
                          <TableRow>
                            <TableCell colSpan={4}
                                      sx={{ fontWeight: 600, backgroundColor: '#f4f4f4' }}>
                              {category}
                            </TableCell>
                          </TableRow>
                          {arr.map(risk => renderRiskRow(risk, category))}
                        </React.Fragment>
                      ))
                    ) : (
                      renderPlaceholder('No risks to display')
                    )}
                  </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </DroppableArea>

        {/* === Drag Overlay === */}
        <DragOverlay>
          {activeRisk && (
            <Paper 
              sx={{ 
                px: 2,
                py: 1,
                backgroundColor: '#fff',
                boxShadow: 4,
                width: 'auto',
                maxWidth: '300px',
                borderRadius: '4px',
                opacity: 0.9,
              }}
            >
              {activeRisk.riskName}
            </Paper>
          )}
        </DragOverlay>

        {/* === Discard Risk Dialog === */}
        <Dialog open={!!confirmDiscardRisk} onClose={() => setConfirmDiscardRisk(null)}>
          <DialogTitle>Confirm Discard</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to discard this risk?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDiscardRisk(null)}>Cancel</Button>
            <Button
              onClick={() => {
                if (confirmDiscardRisk) {
                  handleDiscardRisk(confirmDiscardRisk);
                  setConfirmDiscardRisk(null);
                  setSnackbarMessage('Risk discarded successfully.');
                  setSnackbarSeverity('info');
                  setSnackbarOpen(true);
                }
              }}
            >
              Discard
            </Button>
          </DialogActions>
        </Dialog>

        {/* === Trash Modal === */}
        <TrashModal
          open={showTrashModal}
          onClose={() => setShowTrashModal(false)}
          discardedRisks={Object.values(risks).flat().filter(r => r.status === 'Discarded')}
          onRestore={(risk) => {
            dispatch(moveRiskToSaved(modelId, sectionName, risk));
            setSnackbarMessage('Risk restored successfully!');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
          }}
        />

        {/* === Discard All Unsaved Risks Button === */}
        <Box sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleDiscardClick}
            disabled={unsavedRisks.length === 0}
          >
            Discard All Unsaved Risks
          </Button>
        </Box>

        {/* === Discard All Unsaved Risks Dialog === */}
        <Dialog
          open={openDiscardDialog}
          onClose={handleCancelDiscard}
          aria-labelledby="discard-dialog-title"
        >
          <DialogTitle id="discard-dialog-title">Discard Unsaved Risks?</DialogTitle>
          <DialogContent>
            <Typography>
              You have {unsavedRisks.length} unsaved {unsavedRisks.length === 1 ? 'risk' : 'risks'}. Are you sure you want to discard all unsaved risks?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelDiscard} color="primary">
              Cancel
            </Button>
            <Button onClick={handleConfirmDiscard} color="secondary" variant="contained">
              Discard
            </Button>
          </DialogActions>
        </Dialog>
        {riskToRefine && (
            <RefineRiskModal
              open={openRefineModal}
              onClose={handleCloseRefineModal}
              modelId={modelId}
              sectionName={sectionName}
              existingRisk={riskToRefine}
            />
          )}
              {/* DETAILS DRAWER */}
        <Drawer
          anchor="right"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          PaperProps={{ sx: { width: 450 } }}
        >
          <Box sx={{ width: 350, p: 2 }}>
            {selectedRisk && (
              <>
                {/* Header with Edit and Delete actions */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 1
                  }}
                >
                  <Typography variant="h6">{selectedRisk.riskName}</Typography>
                  {/* <Box>
                    {/* Edit: switch into inline-edit mode *
                    <IconButton onClick={() => handleEditRisk(selectedRisk)}>
                      <EditIcon />
                    </IconButton>
                    {/* Delete: discard this risk *
                    <IconButton
                      onClick={() => setConfirmDiscardRisk(selectedRisk)}
                      sx={{ color: 'red' }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>*/}
                </Box> 

                {/* If in edit mode, show a confirm button */}
                {/* {editingRisk === selectedRisk && (
                  <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
                    <TextField
                      value={editedRiskName}
                      onChange={e => setEditedRiskName(e.target.value)}
                      fullWidth
                      size="small"
                    />
                    <IconButton
                      onClick={() =>
                        handleConfirmEdit({
                          ...selectedRisk,
                          riskName: editedRiskName
                        })
                      }
                    >
                      <CheckIcon />
                    </IconButton>
                  </Box>
                )} */}

                {/* Section & Causes */}
                <Typography variant="subtitle2">Risk Area: {sectionName}</Typography>
                {/* <Accordion sx={{ mt: 2 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography>Causes</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <List dense>
                        {Object.entries(sourceCauses).map(([category, def]) => (
                          <ListItem key={category}>
                            <ListItemText primary={category} secondary={def.description} />
                          </ListItem>
                        ))}
                      </List>
                    </AccordionDetails>
                  </Accordion>
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography>Evaluations</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <List dense>
                        {Object.entries(valueCauses.evaluation || {}).map(([metric, {value}]) => (
                          <ListItem key={metric}>
                            <ListItemText primary={metric} secondary={value} />
                          </ListItem>
                        ))}
                      </List>
                    </AccordionDetails>
                  </Accordion> */}

                {/* Action buttons */}
                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button
                  onClick={() =>
                    dispatch(
                      generateRiskNarrative(
                        modelId,
                        modelName,
                        selectedRisk,
                        modelNarratives
                      )
                    )
                  }
                  disabled={riskNarratives[selectedRisk.id]?.loading}
                >
                  {riskNarratives[selectedRisk.id]?.loading ? 'Loading…' : 'Generate Risk Events'}
                </Button>
                  <Button
                    onClick={() =>
                      dispatch(
                        generateRisksWithRAG(
                          modelId,
                          sectionName,
                          [selectedRisk],
                          risks,
                          modelNarratives
                        )
                      )
                    }
                  >
                    Risk Events Catalogue
                  </Button>

                </Box>

                {/* Save at the bottom */}
                <Box sx={{ mt: 3, textAlign: 'center' }}>
                  <Button
                    variant="contained"
                    onClick={() => handleSaveRisk(selectedRisk)}
                  >
                    Save
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Drawer>

        {/* === Snackbar === */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={(_, reason) => {
            if (reason !== 'clickaway') setSnackbarOpen(false);
          }}
        >
          <Alert severity={snackbarSeverity} onClose={() => setSnackbarOpen(false)} sx={{ width: '100%' }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
        </Box>
      </Box>
    </DndContext>
  );
};

export default RiskCatalogue;